import React, { useState, useEffect } from "react";
import { Box, Paper, Typography } from "@mui/material";
import {
  Droppable,
  Draggable,
  DraggableStateSnapshot,
  DraggableProvided,
  DroppableStateSnapshot,
  DroppableProvided,
} from "@hello-pangea/dnd";
import { useDepthChart } from "../../../contexts/DepthChartContext";
import { DepthChartPlayer, DepthChartPosition } from "../../../types/DepthChartTypes";

interface PositionBoxProps {
  position: DepthChartPosition;
  players: DepthChartPlayer[];
  onRemovePlayer?: (positionId: string, playerId: number) => void;
  isEditable?: boolean;
  onRename?: (id: string, newName: string) => void;
}

export const PositionBox: React.FC<PositionBoxProps> = ({
  position,
  players,
  onRemovePlayer,
  isEditable = true,
  onRename,
}) => {
  const { position_name, position_id } = position;


  // State for renaming
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(position_name);

  // Handle double-click on player to remove
  const handleDoubleClick = (playerId: number) => {
    if (onRemovePlayer && isEditable) {
      onRemovePlayer(position_id, playerId);
    }
  };

  // Handle double-click to edit
  const handleDoubleClickName = () => {
    if (isEditable) {
      setEditValue(position_name);
      setEditing(true);
    }
  };

  // Handle blur or Enter to save
  const handleRenameSave = () => {
    setEditing(false);
    if (onRename && editValue.trim() && editValue !== position_name) {
      onRename(position_id, editValue.trim());
    }
  };

  // Render the position name box
  const renderPositionName = () => (
          <Paper
      elevation={3}
      className="position-drag-handle position-box"
      sx={{
        borderRadius: "20px",
        p: 1.5,
        width: 110,
        height: 40,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        border: "1px solid rgba(255, 255, 255, 0.3)",
        backgroundColor: "#7b2d3a",
        color: "white",
        cursor: isEditable ? "grab" : "default",
        "&:active": {
          cursor: "grabbing"
        }
      }}
    >
      {editing ? (
        <input
          value={editValue}
          autoFocus
          onChange={e => setEditValue(e.target.value)}
          onBlur={handleRenameSave}
          onKeyDown={e => {
            if (e.key === "Enter") handleRenameSave();
            if (e.key === "Escape") setEditing(false);
          }}
          style={{
            width: "90%",
            fontSize: "1rem",
            fontWeight: "bold",
            textAlign: "center",
            background: "#7b2d3a",
            color: "white",
            border: "none",
            outline: "none",
          }}
        />
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: "inherit",
            fontWeight: "bold",
            textShadow: "none",
            lineHeight: 1.2,
            textAlign: "center",
            width: "100%",
            cursor: isEditable ? "pointer" : "default",
            '&:hover': isEditable ? { backgroundColor: "rgba(255,255,255,0.08)", borderRadius: 1 } : {},
          }}
          onDoubleClick={handleDoubleClickName}
        >
          {position_name}
        </Typography>
      )}
    </Paper>
  );

  // Render a player slot
  const renderPlayerSlot = (player: DepthChartPlayer | null, index: number, isDraggable: boolean = false) => {
    if (!player) {
      return (
        <Box
          key={`empty-slot-${index}`}
          sx={{
            height: "24px",
            width: "100%",
          }}
        />
      );
    }

    const playerContent = (
      <Paper
        elevation={2}
        className="player-item"
        sx={{
          py: 0.5,
          px: 1,
          border: "1px solid rgba(255, 255, 255, 0.4)",
          borderRadius: 1,
          backgroundColor: "rgba(40, 40, 40, 0.9)",
          color: "white",
          display: "flex",
          alignItems: "center",
          minHeight: "24px",
          position: "relative",
          "&:hover": {
            backgroundColor: "rgba(50, 50, 50, 0.95)",
            borderColor: "rgba(255, 255, 255, 0.6)",
            cursor: "pointer",
          },
        }}
        onDoubleClick={() => {
          handleDoubleClick(player.id);
        }}
      >
        {/* Jersey number */}
        <Box
          sx={{
            position: "absolute",
            left: -25,
            top: "50%",
            transform: "translateY(-50%)",
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: "#7b2d3a",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            fontSize: "1.3rem",
            fontWeight: "bold",
            border: "1px solid rgba(255, 255, 255, 0.6)",
          }}
        >
          {player.jersey_number || "-"}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.8rem",
              fontWeight: "normal",
              whiteSpace: "nowrap",
              overflow: "visible",
              maxWidth: "none",
            }}
          >
            {player.name}
          </Typography>
        </Box>
      </Paper>
    );

    if (!isDraggable) {
      return playerContent;
    }

    return (
      <Draggable
        key={`depth-player-${position_id}-${player.id}`}
        draggableId={`depth-player-${position_id}-${player.id}`}
        index={index}
      >
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
          return (
            <Box
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              onMouseDown={(e) => {
                e.stopPropagation();
                if (provided.dragHandleProps?.onMouseDown) {
                  provided.dragHandleProps.onMouseDown(e);
                }
              }}
              sx={{
                transform: snapshot.isDragging ? "scale(1.05)" : "scale(1)",
                zIndex: snapshot.isDragging ? 9999 : 1,
              }}
            >
              {playerContent}
            </Box>
          );
        }}
      </Draggable>
    );
  };

  // Render the depth chart slots
  const renderDepthSlots = () => (
    <Box
      sx={{
        position: "absolute",
        top: "100%",
        left: -5,
        width: 160,
        mt: 0.5,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        zIndex: 1,
      }}
    >
      {isEditable ? (
        <Droppable droppableId={`depth-${position_id}`}>
          {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
            return (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  minHeight: "30px",
                  backgroundColor: snapshot.isDraggingOver
                    ? "rgba(60, 60, 60, 0.5)"
                    : "transparent",
                  borderRadius: 1,
                  transition: "background-color 0.2s ease",
                  padding: snapshot.isDraggingOver ? "5px" : "0",
                  border: snapshot.isDraggingOver
                    ? "1px dashed rgba(255, 255, 255, 0.5)"
                    : "none",
                }}
              >
                {players.map((player, index) => renderPlayerSlot(player, index, true))}
                {provided.placeholder}
              </Box>
            );
          }}
        </Droppable>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {players.map((player, index) => renderPlayerSlot(player, index))}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      {isEditable ? (
        <Droppable droppableId={position_id}>
          {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
            return (
              <Box ref={provided.innerRef} {...provided.droppableProps}>
                {renderPositionName()}
                {provided.placeholder}
              </Box>
            );
          }}
        </Droppable>
      ) : (
        renderPositionName()
      )}
      {renderDepthSlots()}
    </Box>
  );
};
